import React, { useEffect, Suspense } from 'react'
import type { ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import {
  ToCalendarHub,
  ToCommsFeed,
  ToCommsChat,
  ToCommsGroup,
  ToProductionTab,
  ToProductionsView,
  ToPeopleHub,
  ToReportsHub,
  ToMovementsHub,
  ToSeasonOverview,
} from './components/routing/legacyRedirects.jsx'
import useAuthStore from './store/authStore.js'
import { useSettingsStore } from './store/settingsStore.js'
import { usePolicyStore } from './store/usePolicyStore.js'
import { useLanguage } from './i18n/index.jsx'
import { setDateLocale } from './utils/format-date.js'
import { setNumberLocale } from './utils/format-currency.js'
import { da as daDateLocale, de as deDateLocale, sv as svDateLocale, nb as nbDateLocale, enGB as enDateLocale } from 'date-fns/locale'
// eslint-disable-next-line no-restricted-imports -- type-only import (erased at build; the subpath rule guards runtime bundle size, N/A here)
import type { Locale } from 'date-fns'
import AppLayout from './components/layout/AppLayout.jsx'
import SimpleLayout from './components/layout/SimpleLayout.jsx'
import SessionExpiredProvider from './components/auth/SessionExpiredProvider.jsx'
import { Spinner, ToastContainer, useToast } from './components/ui/index.jsx'
import UpgradeBanner from './components/ui/UpgradeBanner.jsx'
import DemoLanding from './components/demo/DemoLanding.jsx'
import RouteLoadingSkeleton from './components/RouteLoadingSkeleton.jsx'

// Map i18n UI language → date-fns locale object (month names, weekdays, relative time)
const DATE_LOCALES: Record<string, Locale> = { en: enDateLocale, da: daDateLocale, de: deDateLocale, sv: svDateLocale, no: nbDateLocale }
// Map i18n UI language → BCP-47 tag for Intl.NumberFormat currency/number formatting
const NUMBER_LOCALES: Record<string, string> = { en: 'en-GB', da: 'da-DK', de: 'de-DE', sv: 'sv-SE', no: 'nb-NO' }


const LandingPage           = React.lazy(() => import('./pages/Landing.jsx'))
const WhyBrighella          = React.lazy(() => import('./pages/WhyBrighella.jsx'))
const KioskMarketingPage    = React.lazy(() => import('./pages/Kiosk.jsx'))
const LoginPage             = React.lazy(() => import('./pages/Login.jsx'))
const ForgotPasswordPage    = React.lazy(() => import('./pages/ForgotPassword.jsx'))
const ResetPasswordPage     = React.lazy(() => import('./pages/ResetPassword.jsx'))
const VerifyEmailPage       = React.lazy(() => import('./pages/VerifyEmail.jsx'))
const VerifyEmailConfirmPage = React.lazy(() => import('./pages/VerifyEmailConfirm.jsx'))
const SignupPage             = React.lazy(() => import('./pages/Signup.jsx'))
const PlatformLoginPage     = React.lazy(() => import('./pages/PlatformLogin.jsx'))
const TenantDetailPage      = React.lazy(() => import('./pages/TenantDetail.jsx'))
const AdminAccountsPage     = React.lazy(() => import('./pages/AdminAccounts.jsx'))
const FeedbackAdminPage     = React.lazy(() => import('./pages/FeedbackAdmin.jsx'))
const InviteCodesAdminPage  = React.lazy(() => import('./pages/InviteCodesAdmin.jsx'))
const BillingOverviewPage   = React.lazy(() => import('./pages/BillingOverview.jsx'))
const MetricsDashboardPage  = React.lazy(() => import('./pages/MetricsDashboard.jsx'))
const UsageMeteringPage     = React.lazy(() => import('./pages/UsageMeteringPage.jsx'))
const LogConsolePage        = React.lazy(() => import('./pages/LogConsole.jsx'))
const OnboardingConfigPage  = React.lazy(() => import('./pages/OnboardingConfig.jsx'))
const ProgressDashboardPage  = React.lazy(() => import('./pages/admin/ProgressDashboard.jsx'))
// Calendar Colors & Date Markers (spec 2026-05-27).
const SuperAdminDateMarkersPage = React.lazy(() => import('./pages/SuperAdminDateMarkers.jsx'))
const PlatformModulesPage    = React.lazy(() => import('./pages/PlatformModulesPage.jsx'))
const DemoModulesPage        = React.lazy(() => import('./pages/DemoModulesPage.jsx'))
const DashboardPage         = React.lazy(() => import('./pages/Dashboard.jsx'))
const ItemsPage             = React.lazy(() => import('./pages/Items.jsx'))
const ItemDetailPage        = React.lazy(() => import('./pages/ItemDetail.jsx'))
const WishlistPage          = React.lazy(() => import('./pages/Wishlist.jsx'))
const BundlesPage           = React.lazy(() => import('./pages/Bundles.jsx'))
const ProductionsPage       = React.lazy(() => import('./pages/Productions.jsx'))
const ProductionDetailPage  = React.lazy(() => import('./pages/ProductionDetail.jsx'))
const ScanPage              = React.lazy(() => import('./pages/Scan.jsx'))
// Brighella v2 — Reports Surface (ADR-013 Merge D). /reports is the
// unified subject-typed reports surface; legacy /financial/* + /reports/*
// + /admin/{audit-log,low-stock,stale,lost-damaged} all redirect here.
const ReportsHubPage        = React.lazy(() => import('./pages/ReportsHub.jsx'))
const ImportPage            = React.lazy(() => import('./pages/Import.jsx'))
const TemplatesPage         = React.lazy(() => import('./pages/Templates.jsx'))
const LoansPage             = React.lazy(() => import('./pages/Loans.jsx'))
// Brighella v2 — Movements Hub (ADR-013 Merge E). /movements unifies
// internal / outgoing / incoming loans with one ?direction= tab.
const MovementsHubPage      = React.lazy(() => import('./pages/MovementsHub.jsx'))
const TodosPage             = React.lazy(() => import('./pages/Todos.jsx'))
const DocumentsPage         = React.lazy(() => import('./pages/Documents.jsx'))
const CompanyHandbookPage   = React.lazy(() => import('./pages/CompanyHandbook.jsx'))
const ApiKeysPage           = React.lazy(() => import('./pages/ApiKeys.jsx'))
const LocationsPage         = React.lazy(() => import('./pages/Locations.jsx'))
const SitesPage             = React.lazy(() => import('./pages/Sites.jsx'))
const SiteDetailPage        = React.lazy(() => import('./pages/SiteDetail.jsx'))
const VendorPortalCredentialsPage = React.lazy(() => import('./pages/VendorPortalCredentials.jsx'))
const SiteTemplatesPage     = React.lazy(() => import('./pages/SiteTemplates.jsx'))
const LocationDetailPage    = React.lazy(() => import('./pages/LocationDetail.jsx'))
// Brighella v2 — Calendar Hub (ADR-013 Merge A). Mounted at /v2/calendar
// during build-out. Once feature-complete, /calendar swaps to this hub
// and old routes redirect.
const CalendarHubPage       = React.lazy(() => import('./pages/CalendarHub.jsx'))
const ItemStickerPage       = React.lazy(() => import('./pages/ItemSticker.jsx'))
const BulkStickerPage       = React.lazy(() => import('./pages/BulkSticker.jsx'))
const PrintCenterPage       = React.lazy(() => import('./pages/PrintCenter.jsx'))
const LocationPrintCenterPage = React.lazy(() => import('./pages/LocationPrintCenter.jsx'))
const PrintCenterHubPage     = React.lazy(() => import('./pages/PrintCenterHub.jsx'))
const QrResolvePage          = React.lazy(() => import('./pages/QrResolve.jsx'))
const IdCardPage             = React.lazy(() => import('./pages/IdCard.jsx'))
const MyReferralsPage        = React.lazy(() => import('./pages/MyReferrals.jsx'))
const HelpPage               = React.lazy(() => import('./pages/Help.jsx'))
// Brighella v2 — People Hub (ADR-013 Merge C). /people consolidates
// /users, /admin/pto, /admin/freelancer-timesheets, /crew/workload.
const PeopleHubPage         = React.lazy(() => import('./pages/PeopleHub.jsx'))
const UserActivityPage      = React.lazy(() => import('./pages/UserActivity.jsx'))
const AcceptInvitePage      = React.lazy(() => import('./pages/AcceptInvite.jsx'))
const SuperAdminPage        = React.lazy(() => import('./pages/SuperAdmin.jsx'))
const BillingSettingsPage   = React.lazy(() => import('./pages/BillingSettings.jsx'))
const QrFieldViewPage       = React.lazy(() => import('./pages/QrFieldView.jsx'))
const KioskHomePage         = React.lazy(() => import('./pages/KioskHome.jsx'))
const KioskSortPage         = React.lazy(() => import('./pages/KioskSort.jsx'))
const KioskInventoryPage    = React.lazy(() => import('./pages/KioskInventory.jsx'))
const KioskDisplayPage      = React.lazy(() => import('./pages/KioskDisplay.jsx'))
const KioskSharePage        = React.lazy(() => import('./pages/KioskShare.jsx'))
const NotificationCenterPage = React.lazy(() => import('./pages/NotificationCenter.jsx'))
const NetworkPage           = React.lazy(() => import('./pages/Network.jsx'))
const VenueCatalogPage      = React.lazy(() => import('./pages/VenueCatalog.jsx'))
const NeedsBoardPage        = React.lazy(() => import('./pages/NeedsBoard.jsx'))
const MarketplacePage       = React.lazy(() => import('./pages/Marketplace.jsx'))
const SharedViewPage        = React.lazy(() => import('./pages/SharedView.jsx'))
const SharedIdCardPage      = React.lazy(() => import('./pages/SharedIdCard.jsx'))
const SharedInfoPage        = React.lazy(() => import('./pages/SharedInfoPage.jsx'))
const TemplateMarketplacePage = React.lazy(() => import('./pages/TemplateMarketplace.jsx'))

const MaintenanceDashboardPage = React.lazy(() => import('./pages/MaintenanceDashboard.jsx'))
const PullSheetPage         = React.lazy(() => import('./pages/PullSheet.jsx'))
const EventsPage            = React.lazy(() => import('./pages/Events.jsx'))
const EventDetailPage       = React.lazy(() => import('./pages/EventDetail.jsx'))
const BudgetsPage           = React.lazy(() => import('./pages/Budgets.jsx'))
const MyHoursPage           = React.lazy(() => import('./pages/MyHours.jsx'))
// Season Canvas (revived AKOS) now lives as the default tab inside PeopleHub
// (Merge C+). /season-overview redirects there; PeopleHub lazy-loads the page.
const PtoSettingsPage       = React.lazy(() => import('./pages/admin/PtoSettings.jsx'))
const SpacesPage            = React.lazy(() => import('./pages/Spaces.jsx'))
const SpaceDetailPage       = React.lazy(() => import('./pages/SpaceDetail.jsx'))
const SimpleHomePage        = React.lazy(() => import('./pages/SimpleHome.jsx'))
const SimpleCheckInOutPage  = React.lazy(() => import('./pages/SimpleCheckInOut.jsx'))
const SimplePullSheetSelectPage = React.lazy(() => import('./pages/SimplePullSheetSelect.jsx'))
const SupportAdminPage      = React.lazy(() => import('./pages/SupportAdmin.jsx'))
const SupportTicketsPage    = React.lazy(() => import('./pages/SupportTickets.jsx'))
const SupportTicketDetailPage = React.lazy(() => import('./pages/SupportTicketDetail.jsx'))
// CommsDmPage + SpaceViewPage superseded by shell redirects in Task 3 (Phase 2).
const CommsGuestJoinPage    = React.lazy(() => import('./pages/CommsGuestJoin.jsx'))
const CommsShell            = React.lazy(() => import('./components/comms/CommsShell.jsx'))
const CommsFeed             = React.lazy(() => import('./pages/comms/CommsFeed.jsx'))
const CommsChat             = React.lazy(() => import('./pages/comms/CommsChat.jsx'))
const CommsGroups           = React.lazy(() => import('./pages/comms/CommsGroups.jsx'))
const ContactsList          = React.lazy(() => import('./components/comms/ContactsList.jsx'))
const GuestSchedulePage     = React.lazy(() => import('./pages/GuestSchedule.jsx'))
const QAReportsPage         = React.lazy(() => import('./pages/QAReports.jsx'))
const SystemHealthPage      = React.lazy(() => import('./pages/super-admin/SystemHealthPage.jsx'))
const PlanEditorPage        = React.lazy(() => import('./pages/super-admin/PlanEditorPage.jsx'))
const VenueModulesPage      = React.lazy(() => import('./pages/super-admin/VenueModulesPage.jsx'))
const CalendarProjectionDiagPage = React.lazy(() => import('./pages/CalendarProjectionDiag.jsx'))
const NotFoundPage          = React.lazy(() => import('./pages/NotFound.jsx'))
const RidersPage            = React.lazy(() => import('./pages/Riders.jsx'))
const RiderEditorPage       = React.lazy(() => import('./pages/RiderEditor.jsx'))
const ArtistsPage           = React.lazy(() => import('./pages/Artists.jsx'))
const TourList              = React.lazy(() => import('./pages/TourList.jsx'))
const TourWorkspace         = React.lazy(() => import('./pages/TourWorkspace.jsx'))
const TourStopWorkspace     = React.lazy(() => import('./pages/TourStopWorkspace'))
const SharedRiderPage       = React.lazy(() => import('./pages/SharedRider.jsx'))
const CompanyNoticePublic   = React.lazy(() => import('./pages/CompanyNoticePublic.jsx'))
const SsoCallbackPage       = React.lazy(() => import('./pages/SsoCallback.jsx'))
const PrivacyPolicyPage     = React.lazy(() => import('./pages/PrivacyPolicy.jsx'))
const TermsOfServicePage    = React.lazy(() => import('./pages/TermsOfService.jsx'))
const DataProcessingAgreementPage = React.lazy(() => import('./pages/DataProcessingAgreement.jsx'))
const NfcTagsPage                = React.lazy(() => import('./pages/NfcTags.jsx'))
const RentVenuePage              = React.lazy(() => import('./pages/RentVenue.jsx'))
const VenueInquiriesAdminPage    = React.lazy(() => import('./pages/VenueInquiriesAdmin.jsx'))
const GreenBookHubPage           = React.lazy(() => import('./pages/GreenBookHub.jsx'))
const PublicSustainabilityPage   = React.lazy(() => import('./pages/PublicSustainability.jsx'))
const SettingsHub                = React.lazy(() => import('./pages/SettingsHub.jsx'))
const CablesPage                 = React.lazy(() => import('./pages/Cables.jsx'))

import { ErrorBoundary, RouteErrorBoundary } from './components/ErrorBoundary.jsx'
import CookieBanner from './components/ui/CookieBanner.jsx'

import './components/ui/ui.css'
import './pages/Dashboard.css'

// §4.9: every guard below used to subscribe to the whole authStore via
// `useAuthStore()`, which re-rendered the entire protected tree (and
// stole focus from modals) on every unrelated store mutation — toggling
// simple mode, changing plan, updating notifications, etc. Selectors
// scope the subscription to the two fields each guard actually reads.
function RequireAuth({ children }: { children: ReactNode }) {
  const user = useAuthStore(s => s.user)
  const loading = useAuthStore(s => s.loading)
  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100dvh' }}>
      <Spinner size={36} />
    </div>
  )
  if (!user) return <Navigate to="/login" replace />
  return children
}

// Requires local_admin or global_admin role
function RequireAdmin({ children }: { children: ReactNode }) {
  const user = useAuthStore(s => s.user)
  const loading = useAuthStore(s => s.loading)
  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100dvh' }}>
      <Spinner size={36} />
    </div>
  )
  const ok = ['local_admin', 'global_admin'].includes(user?.role ?? '') || user?.is_global_admin
  if (!ok) return <Navigate to="/" replace />
  return children
}

// For /super-admin routes — redirects unauthenticated users to /platform-login (not /login)
function RequirePlatformAdmin({ children }: { children: ReactNode }) {
  const user = useAuthStore(s => s.user)
  const loading = useAuthStore(s => s.loading)
  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100dvh' }}>
      <Spinner size={36} />
    </div>
  )
  if (!user) return <Navigate to="/platform-login" replace />
  if (!user?.is_global_admin && user?.role !== 'global_admin') return <Navigate to="/" replace />
  return children
}

// Requires a specific feature module to be enabled — shows upgrade prompt for plan-gated features.
//
// B-3 (D15-frontend-shared §3.1.5): this gate previously fail-OPENed during
// the ~50-200 ms bootstrap window where enabledModules is still null. On a
// deep link to a gated route, the page briefly rendered (flash of
// unauthorized content) before the settings load resolved and decided the
// gate. It also ignored the super-admin kill switch — a module the platform
// admin had locked off could still mount if tenant settings allowed it.
//
// New contract:
//   1. settingsLoaded === false → show RouteLoadingSkeleton (no children).
//   2. Module appears in platformDisabledModules OR platformModules[key]===false
//      → redirect to "/" (skip the upgrade banner; no plan can re-enable it).
//   3. enabledModules is null OR enabledModules[module] === false →
//      show upgrade banner on free/starter, else redirect to "/".
//   4. Otherwise → render children.
export function RequireModule({ module, children }: { module: string; children: ReactNode }) {
  const settingsLoaded = useSettingsStore(s => s.settingsLoaded)
  const platformModules = useSettingsStore(s => s.platformModules)
  const platformDisabledModules = useSettingsStore(s => s.platformDisabledModules)
  const planName = useSettingsStore(s => s.planName)
  const policyModules = usePolicyStore(s => s.modules)

  // When the canonical policy snapshot has loaded it is authoritative — it
  // collapses plan entitlement, the platform kill switch and the per-tenant
  // veto into one verdict. The legacy fallback below runs only while the
  // snapshot has not loaded.
  const verdict = policyModules ? policyModules[module] : null
  if (verdict) {
    if (verdict.allowed) return children
    // plan_not_entitled is the only upgradeable denial — every other reason
    // (platform / user) is a hard lock that no plan can re-enable.
    if (verdict.reason === 'plan_not_entitled') {
      return <UpgradeBanner planName={planName ?? undefined} module={module} />
    }
    return <Navigate to="/" replace />
  }

  // ── Legacy fallback — snapshot not loaded ──────────────────────────────
  // Honors only the platform kill switches client-side; plan entitlement
  // comes from the snapshot. The tenant module toggle was removed in Phase 6.
  if (!settingsLoaded) return <RouteLoadingSkeleton />
  const isPlatformKillSwitched =
    (platformDisabledModules && Object.prototype.hasOwnProperty.call(platformDisabledModules, module)) ||
    (platformModules && platformModules[module] === false)
  if (isPlatformKillSwitched) return <Navigate to="/" replace />
  return children
}

// Requires a specific feature flag from the plan — shows upgrade prompt if not available.
//
// B-3: same bootstrap-window fix as RequireModule. Without settingsLoaded
// gating, planFeatures defaults to {} and the upgrade banner flashes for
// every protected route during initial load even when the user actually
// has the feature.
export function RequireFeature({ feature, children }: { feature: string; children: ReactNode }) {
  const settingsLoaded = useSettingsStore(s => s.settingsLoaded)
  const planFeatures = useSettingsStore(s => s.planFeatures)
  const planName = useSettingsStore(s => s.planName)
  const subStatus = useSettingsStore(s => s.subStatus)
  if (!settingsLoaded) return <RouteLoadingSkeleton />
  if (!planFeatures?.[feature]) {
    // BILL-5: a past_due subscription demotes gating instantly — the denial
    // is a payment problem, not a plan-tier problem, and the copy must say so
    // (the "not on your plan" message gaslit staff whose plan DOES include it).
    return (
      <UpgradeBanner
        planName={planName ?? undefined}
        module={feature}
        reason={subStatus === 'past_due' ? 'past_due' : 'plan'}
      />
    )
  }
  return children
}

// Shorthand wrappers for module-guarded pages
const ModPage = ({ module, children }: { module: string; children: ReactNode }) => <Page><RequireModule module={module}>{children}</RequireModule></Page>

// Kiosk route — marketing page for visitors, manager hub for authenticated users.
// Single resolver so /kiosk works as a public marketing URL AND as the in-app
// kiosk-displays hub without route-pattern collision.
function KioskRoute() {
  const user = useAuthStore(s => s.user)
  const loading = useAuthStore(s => s.loading)
  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100dvh' }}>
      <Spinner size={36} />
    </div>
  )
  if (!user) return <KioskMarketingPage />
  return (
    <RequireFeature feature="kiosk">
      <RequireModule module="kiosk">
        <RouteErrorBoundary>
          <KioskHomePage />
        </RouteErrorBoundary>
      </RequireModule>
    </RequireFeature>
  )
}

// Root route — landing page for visitors, dashboard for authenticated users
function RootRoute() {
  const user = useAuthStore(s => s.user)
  const loading = useAuthStore(s => s.loading)
  const simpleMode = useAuthStore(s => s.simpleMode)
  if (loading) return (
    // A <main> landmark even while auth resolves — axe `landmark-one-main`
    // audits `/` and would otherwise see zero landmarks during this state
    // (the loading spinner is what renders before LandingPage's own <main>).
    <main style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100dvh' }}>
      <Spinner size={36} />
    </main>
  )
  if (!user) return <LandingPage />
  if (simpleMode) return <Navigate to="/simple" replace />
  const defaultPage = user.ui_preferences?.default_page
  if (defaultPage && defaultPage !== '/') return <Navigate to={defaultPage} replace />
  return <AppLayout><RouteErrorBoundary><DashboardPage /></RouteErrorBoundary></AppLayout>
}

const Page        = ({ children }: { children: ReactNode }) => <RequireAuth><AppLayout><RouteErrorBoundary><Suspense fallback={<RouteLoadingSkeleton />}>{children}</Suspense></RouteErrorBoundary></AppLayout></RequireAuth>
const AdminP      = ({ children }: { children: ReactNode }) => <RequireAuth><RequireAdmin><AppLayout><RouteErrorBoundary><Suspense fallback={<RouteLoadingSkeleton />}>{children}</Suspense></RouteErrorBoundary></AppLayout></RequireAdmin></RequireAuth>
const SimplePage  = ({ children }: { children: ReactNode }) => <RequireAuth><SimpleLayout><RouteErrorBoundary><Suspense fallback={<RouteLoadingSkeleton />}>{children}</Suspense></RouteErrorBoundary></SimpleLayout></RequireAuth>
const PlatformLayout = () => (
  <RequirePlatformAdmin>
    {/* W2: per-route boundary + Suspense so a super-admin page crash/chunk-load
        failure shows a recoverable in-shell error instead of resetting the app. */}
    <AppLayout><RouteErrorBoundary><Suspense fallback={<RouteLoadingSkeleton />}><Outlet /></Suspense></RouteErrorBoundary></AppLayout>
  </RequirePlatformAdmin>
)

// Global toast container — mounted once at the app root so pages don't need
// their own <ToastContainer>. Pages that still have one will be cleaned up
// by the codemod; this instance is the canonical one.
function GlobalToastContainer() {
  const { toasts, remove } = useToast()
  return <ToastContainer toasts={toasts} remove={remove} />
}

export default function App() {
  const fetchMe                    = useAuthStore(s => s.fetchMe)
  const installFocusRefreshListener = useAuthStore(s => s.installFocusRefreshListener)
  const { lang } = useLanguage()
  useEffect(() => { fetchMe() }, [])
  // Proactively refresh the access token when the user returns to a tab
  // that's been hidden long enough for the JWT to expire. Eliminates the
  // "leave page open → 'failed to load data'" UX bug at its root by
  // refreshing ONCE deliberately before React Query's refetchOnWindowFocus
  // fires its parallel refetch storm. See authStore.installFocusRefreshListener.
  useEffect(() => installFocusRefreshListener(), [installFocusRefreshListener])

  // a11y: keep <html lang> in sync with the user's active locale so screen readers
  // pick the right pronunciation profile. Also wire date/number formatters to the
  // same locale so fmtDate() produces German month names when lang === 'de', etc.
  useEffect(() => {
    const code = lang || 'en'
    document.documentElement.lang = code
    setDateLocale(DATE_LOCALES[code] || DATE_LOCALES.en)
    setNumberLocale(NUMBER_LOCALES[code] || NUMBER_LOCALES.en)
  }, [lang])

  return (
    <ErrorBoundary>
    <BrowserRouter>
      <GlobalToastContainer />
      <SessionExpiredProvider>
      <Suspense fallback={
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100dvh' }}>
          <Spinner size={36} />
        </div>
      }>
      <Routes>
        {/* Public — no auth */}
        <Route path="/share/:token"       element={<RouteErrorBoundary><SharedViewPage /></RouteErrorBoundary>} />
        <Route path="/guest/:token"       element={<RouteErrorBoundary><SharedViewPage /></RouteErrorBoundary>} />
        <Route path="/id-card/:token"     element={<RouteErrorBoundary><SharedIdCardPage /></RouteErrorBoundary>} />
        <Route path="/info/:token"        element={<RouteErrorBoundary><SharedInfoPage /></RouteErrorBoundary>} />
        <Route path="/join/:token"        element={<RouteErrorBoundary><CommsGuestJoinPage /></RouteErrorBoundary>} />
        <Route path="/rider-share/:token" element={<RouteErrorBoundary><SharedRiderPage /></RouteErrorBoundary>} />
        <Route path="/notice/:token"      element={<RouteErrorBoundary><React.Suspense fallback={null}><CompanyNoticePublic /></React.Suspense></RouteErrorBoundary>} />
        <Route path="/public/sustainability/:slug" element={<RouteErrorBoundary><PublicSustainabilityPage /></RouteErrorBoundary>} />

        {/* SSO callback — must be public, no auth */}
        <Route path="/auth/sso/:provider/callback" element={<SsoCallbackPage />} />

        {/* Public */}
        <Route path="/login"              element={<LoginPage />} />
        <Route path="/signup"             element={<SignupPage />} />
        <Route path="/forgot-password"    element={<ForgotPasswordPage />} />
        <Route path="/reset-password"     element={<ResetPasswordPage />} />
        <Route path="/platform-login"     element={<PlatformLoginPage />} />
        <Route path="/accept-invite"      element={<AcceptInvitePage />} />
        <Route path="/verify-email"       element={<VerifyEmailPage />} />
        <Route path="/verify-email/confirm" element={<VerifyEmailConfirmPage />} />
        <Route path="/why-stagewise"     element={<WhyBrighella />} />
        <Route path="/privacy"            element={<PrivacyPolicyPage />} />
        <Route path="/terms"              element={<TermsOfServicePage />} />
        <Route path="/dpa"                element={<DataProcessingAgreementPage />} />

        {/* Root — landing (public) or dashboard (authenticated) */}
        <Route path="/"                   element={<RootRoute />} />
        {/* Inventory — always enabled (core module) */}
        <Route path="/items"              element={<Page><ItemsPage /></Page>} />
        <Route path="/items/:id"          element={<Page><ItemDetailPage /></Page>} />
        <Route path="/wishlist"           element={<Page><WishlistPage /></Page>} />
        <Route path="/items/:id/sticker"  element={<RequireAuth><RouteErrorBoundary><ItemStickerPage /></RouteErrorBoundary></RequireAuth>} />
        <Route path="/items/stickers"    element={<Page><BulkStickerPage /></Page>} />
        <Route path="/items/print-center" element={<Page><PrintCenterHubPage /></Page>} />
        <Route path="/items/print-center/items" element={<Page><PrintCenterPage /></Page>} />
        <Route path="/me/id-card" element={<Page><IdCardPage /></Page>} />
        {/* ADR-014 D22 — user-facing referral dashboard. */}
        <Route path="/me/referrals" element={<Page><MyReferralsPage /></Page>} />
        <Route path="/items/print-center/locations" element={<Page><LocationPrintCenterPage /></Page>} />
        <Route path="/help" element={<Page><HelpPage /></Page>} />
        <Route path="/help/:id" element={<Page><HelpPage /></Page>} />
        <Route path="/bundles"            element={<Page><BundlesPage /></Page>} />
        <Route path="/templates"          element={<Page><TemplatesPage /></Page>} />
        <Route path="/templates/marketplace" element={<Page><TemplateMarketplacePage /></Page>} />
        {/* Brighella v2 (ADR-013 Merge E) — Movements Hub.
            /loans, /borrowed, /network/loans all funnel into /movements
            with ?direction= preserved. */}
        <Route path="/movements"          element={<Page><MovementsHubPage /></Page>} />
        <Route path="/loans"              element={<ToMovementsHub direction="internal" />} />
        <Route path="/locations"          element={<Page><LocationsPage /></Page>} />
        {/* Legacy URL — Network Inventory is now the Credentials tab of /sites. */}
        <Route path="/network-inventory"  element={<Navigate to="/sites?tab=credentials" replace />} />
        {/* Wave 2 — Site Setup Network Inventory v2 */}
        <Route path="/sites"                       element={<Page><SitesPage /></Page>} />
        <Route path="/sites/:siteId"               element={<Page><SiteDetailPage /></Page>} />
        <Route path="/tenant/vendor-credentials"   element={<Page><VendorPortalCredentialsPage /></Page>} />
        <Route path="/tenant/site-templates"       element={<Page><SiteTemplatesPage /></Page>} />
        <Route path="/locations/:id"      element={<Page><LocationDetailPage /></Page>} />
        <Route path="/borrowed"           element={<ToMovementsHub direction="incoming" />} />

        {/* Productions module */}
        <Route path="/productions"        element={<ModPage module="productions"><ProductionsPage /></ModPage>} />
        <Route path="/productions/:id"    element={<ModPage module="productions"><ProductionDetailPage /></ModPage>} />
        <Route path="/events"                 element={<ModPage module="productions"><EventsPage /></ModPage>} />
        <Route path="/events/:id"             element={<ModPage module="productions"><EventDetailPage /></ModPage>} />
        <Route path="/budgets"                element={<ModPage module="productions"><BudgetsPage /></ModPage>} />
        {/* Brighella v2 (ADR-013 Merge B) — production-as-workspace.
            pull-sheet / call-sheet / budget are tabs on the detail page now;
            legacy deep links redirect with ?tab= preserved. */}
        <Route path="/productions/:id/budget"     element={<ToProductionTab tab="budget" />} />
        <Route path="/productions/:id/pull-sheet" element={<ToProductionTab tab="pull-sheet" />} />
        <Route path="/productions/:id/call-sheet" element={<ToProductionTab tab="call-sheet" />} />
        <Route path="/department-orders" element={<ToProductionsView view="department-orders" />} />
        {/* Brighella v2 Calendar Hub — ADR-013 Merge A. /calendar IS the hub.
            6 legacy routes (+ the build-out alias /v2/calendar) redirect here
            preserving search + hash so deep links still work. */}
        <Route path="/calendar"           element={<ModPage module="productions"><CalendarHubPage /></ModPage>} />
        <Route path="/v2/calendar"        element={<ToCalendarHub />} />
        <Route path="/calendar/overview"  element={<ToCalendarHub />} />
        <Route path="/daily"              element={<ToCalendarHub />} />
        <Route path="/schedule"           element={<ToCalendarHub />} />
        <Route path="/season"             element={<ToSeasonOverview />} />
        <Route path="/season-overview"    element={<ToSeasonOverview />} />
        <Route path="/spaces"              element={<ModPage module="productions"><SpacesPage /></ModPage>} />
        <Route path="/spaces/:id"          element={<ModPage module="productions"><SpaceDetailPage /></ModPage>} />
        <Route path="/green-book"         element={<ModPage module="sustainability"><GreenBookHubPage /></ModPage>} />
        <Route path="/my-hours"           element={<ModPage module="crew"><MyHoursPage /></ModPage>} />
        <Route path="/external-productions" element={<ToProductionsView view="external" />} />
        <Route path="/riders"             element={<ModPage module="productions"><RidersPage /></ModPage>} />
        <Route path="/riders/:id"         element={<ModPage module="productions"><RiderEditorPage /></ModPage>} />
        <Route path="/artists"            element={<ModPage module="productions"><ArtistsPage /></ModPage>} />
        <Route path="/tours"     element={<ModPage module="touring"><TourList /></ModPage>} />
        <Route path="/tours/:id" element={<ModPage module="touring"><TourWorkspace /></ModPage>} />
        <Route path="/tours/:id/stops/:stopId" element={<ModPage module="touring"><TourStopWorkspace /></ModPage>} />
        <Route path="/incoming-productions" element={<ToProductionsView view="incoming" />} />
        <Route path="/network"            element={<ModPage module="network"><NetworkPage /></ModPage>} />
        <Route path="/network/:venue/catalog" element={<ModPage module="network"><VenueCatalogPage /></ModPage>} />
        <Route path="/network/loans"      element={<ToMovementsHub direction="outgoing" />} />
        <Route path="/network/requests"   element={<ModPage module="network"><NeedsBoardPage /></ModPage>} />
        <Route path="/marketplace"       element={<ModPage module="network"><MarketplacePage /></ModPage>} />
        {/* Brighella v2 (ADR-013 Merge C) — People Hub.
            /users, /admin/pto, /admin/freelancer-timesheets, /crew/workload
            consolidate into /people with tabs (search + hash preserved). */}
        <Route path="/people"             element={<ModPage module="crew"><PeopleHubPage /></ModPage>} />
        <Route path="/crew/workload"      element={<ToPeopleHub tab="workload" />} />
        <Route path="/crew/schedule"      element={<ToCalendarHub />} />
        <Route path="/crew-schedule"      element={<ToCalendarHub />} />

        {/* Cables module — FEAT-002 */}
        <Route path="/cables"             element={<ModPage module="cables"><CablesPage /></ModPage>} />
        <Route path="/cables/:id"         element={<ModPage module="cables"><CablesPage /></ModPage>} />

        {/* Scanning module */}
        <Route path="/scan/:type?/:id?"   element={<ModPage module="scanning"><ScanPage /></ModPage>} />
        <Route path="/s/:code"            element={<Page><QrResolvePage /></Page>} />
        <Route path="/scan/result/:code"  element={<ModPage module="scanning"><QrFieldViewPage /></ModPage>} />

        {/* Messaging module — single Ziik-style comms shell (Phase 2). */}
        <Route path="/feed" element={<ToCommsFeed />} />
        <Route path="/comms" element={<ModPage module="messaging"><CommsShell /></ModPage>}>
          <Route index element={<Navigate to="/comms/feed" replace />} />
          <Route path="feed"          element={<CommsFeed />} />
          <Route path="groups"        element={<CommsGroups />} />
          <Route path="groups/:id"    element={<CommsGroups />} />
          <Route path="chat"          element={<CommsChat />} />
          <Route path="chat/:id"      element={<CommsChat />} />
          <Route path="contacts"      element={<ContactsList />} />
        </Route>
        {/* Legacy redirects → into the shell (bookmarks survive). */}
        <Route path="/comms/dm"              element={<ToCommsChat />} />
        <Route path="/comms/dm/:id"          element={<ToCommsChat />} />
        <Route path="/comms/group/:id"       element={<ToCommsGroup />} />
        <Route path="/comms/channel/:id"     element={<ToCommsGroup />} />
        <Route path="/comms/space/:id"       element={<ToCommsGroup />} />
        <Route path="/comms/company"         element={<ToCommsGroup />} />
        <Route path="/comms/department/:id"  element={<ToCommsGroup />} />
        {/* ROS-3: read-only schedule for schedule-scoped guests. Standalone
            (no AppLayout) so it stays minimal on mobile and prints clean. */}
        <Route path="/guest-schedule"     element={<RequireAuth><RouteErrorBoundary><GuestSchedulePage /></RouteErrorBoundary></RequireAuth>} />

        {/* Tasks module */}
        <Route path="/todos"              element={<ModPage module="tasks"><TodosPage /></ModPage>} />
        <Route path="/documents"          element={<ModPage module="documents"><DocumentsPage /></ModPage>} />
        <Route path="/handbook"           element={<Page><CompanyHandbookPage /></Page>} />

        {/* Always available */}
        <Route path="/notifications"      element={<Page><NotificationCenterPage /></Page>} />
        <Route path="/support"            element={<Page><SupportTicketsPage /></Page>} />
        <Route path="/support/:id"        element={<Page><SupportTicketDetailPage /></Page>} />

        {/* Simple Mode — simplified inventory UI */}
        <Route path="/simple"                    element={<SimplePage><SimpleHomePage /></SimplePage>} />
        <Route path="/simple/scan/:type?/:id?"   element={<SimplePage><ScanPage /></SimplePage>} />
        <Route path="/simple/items"              element={<SimplePage><ItemsPage /></SimplePage>} />
        <Route path="/simple/items/:id"          element={<SimplePage><ItemDetailPage /></SimplePage>} />
        <Route path="/simple/loans"              element={<SimplePage><LoansPage /></SimplePage>} />
        <Route path="/simple/pull-sheet"         element={<SimplePage><SimplePullSheetSelectPage /></SimplePage>} />
        <Route path="/simple/pull-sheet/:id"     element={<SimplePage><PullSheetPage /></SimplePage>} />
        <Route path="/simple/check"              element={<SimplePage><SimpleCheckInOutPage /></SimplePage>} />
        <Route path="/simple/locations"          element={<SimplePage><LocationsPage /></SimplePage>} />
        <Route path="/simple/locations/:id"      element={<SimplePage><LocationDetailPage /></SimplePage>} />

        {/* Kiosk — no AppLayout, Enterprise only, module-toggleable.
            Public visitors see the marketing page; auth'd users see the manager hub.
            Single resolver lives at component KioskRoute. */}
        <Route path="/kiosk"              element={<KioskRoute />} />
        <Route path="/kiosk/sort"         element={<RequireAuth><RequireFeature feature="kiosk"><RequireModule module="kiosk"><RouteErrorBoundary><KioskSortPage /></RouteErrorBoundary></RequireModule></RequireFeature></RequireAuth>} />
        <Route path="/kiosk/inventory"    element={<RequireAuth><RequireFeature feature="kiosk"><RequireModule module="kiosk"><RouteErrorBoundary><KioskInventoryPage /></RouteErrorBoundary></RequireModule></RequireFeature></RequireAuth>} />
        <Route path="/kiosk/display"      element={<RequireAuth><RequireFeature feature="kiosk"><RequireModule module="kiosk"><RouteErrorBoundary><KioskDisplayPage /></RouteErrorBoundary></RequireModule></RequireFeature></RequireAuth>} />
        <Route path="/kiosk/display/:id"  element={<RequireAuth><RequireFeature feature="kiosk"><RequireModule module="kiosk"><RouteErrorBoundary><KioskDisplayPage /></RouteErrorBoundary></RequireModule></RequireFeature></RequireAuth>} />
        <Route path="/kiosk/share/:token" element={<RouteErrorBoundary><KioskSharePage /></RouteErrorBoundary>} />

        {/* Admin only */}
        <Route path="/users"              element={<ToPeopleHub tab="directory" />} />
        <Route path="/users/:id/activity" element={<AdminP><UserActivityPage /></AdminP>} />
        <Route path="/items/import"       element={<AdminP><ImportPage /></AdminP>} />
        <Route path="/admin/calendar-projection" element={<AdminP><CalendarProjectionDiagPage /></AdminP>} />
        {/* Calendar Colors & Date Markers (spec 2026-05-27) — manager+ view. */}
        <Route path="/admin/date-markers" element={<AdminP><SuperAdminDateMarkersPage /></AdminP>} />
        <Route path="/admin/plans" element={<AdminP><PlanEditorPage /></AdminP>} />
        <Route path="/admin/venue-modules" element={<AdminP><VenueModulesPage /></AdminP>} />
        {/* Brighella v2 (ADR-013 Merge D) — Reports verb as first-class.
            /reports?type=…&view=… replaces 14 legacy paths. Each old
            URL redirects with its type+view set so deep links survive. */}
        <Route path="/reports"            element={<AdminP><ReportsHubPage /></AdminP>} />
        <Route path="/financial"          element={<ToReportsHub type="financial" view="overview" />} />
        <Route path="/financial/explorer" element={<ToReportsHub type="financial" view="explorer" />} />
        <Route path="/admin"              element={<AdminP><SettingsHub /></AdminP>} />
        <Route path="/admin/pto"          element={<ToPeopleHub tab="absences" />} />
        <Route path="/admin/balance-types" element={<AdminP><PtoSettingsPage /></AdminP>} />
        <Route path="/admin/stale"        element={<ToReportsHub type="inventory" view="stale" />} />
        <Route path="/admin/audit-log"   element={<ToReportsHub type="admin" view="audit-log" />} />
        <Route path="/admin/api-keys"    element={<AdminP><ApiKeysPage /></AdminP>} />
        <Route path="/settings/billing"   element={<AdminP><BillingSettingsPage /></AdminP>} />
        <Route path="/maintenance"        element={<Page><MaintenanceDashboardPage /></Page>} />
        <Route path="/admin/low-stock"    element={<ToReportsHub type="inventory" view="low-stock" />} />
        <Route path="/admin/lost-damaged" element={<ToReportsHub type="inventory" view="lost-damaged" />} />
        <Route path="/nfc-tags"           element={<Page><NfcTagsPage /></Page>} />
        <Route path="/admin/ai"           element={<Navigate to="/admin?tab=ai-settings" replace />} />
        <Route path="/rent"               element={<RouteErrorBoundary><RentVenuePage /></RouteErrorBoundary>} />
        <Route path="/admin/venue-inquiries" element={<AdminP><VenueInquiriesAdminPage /></AdminP>} />
        <Route path="/admin/freelancer-timesheets" element={<ToPeopleHub tab="timesheets" />} />
        <Route path="/financial/insurance"         element={<ToReportsHub type="financial" view="insurance" />} />
        <Route path="/financial/forecast"          element={<ToReportsHub type="financial" view="forecast" />} />
        <Route path="/financial/cash-flow"         element={<ToReportsHub type="financial" view="cash-flow" />} />
        <Route path="/financial/season-comparison" element={<ToReportsHub type="financial" view="season-comparison" />} />
        <Route path="/financial/petty-cash"        element={<ToReportsHub type="financial" view="petty-cash" />} />
        <Route path="/financial/budget-revisions"  element={<ToReportsHub type="financial" view="budget-revisions" />} />
        <Route path="/financial/utilization"       element={<ToReportsHub type="financial" view="utilization" />} />
        <Route path="/reports/cost-comparison"     element={<ToReportsHub type="financial" view="cost-comparison" />} />
        <Route path="/reports/depreciation"        element={<ToReportsHub type="financial" view="depreciation" />} />

        {/* Global admin — platform layout shell */}
        <Route
          path="/super-admin"
          element={<PlatformLayout />}
        >
          <Route index                   element={<SuperAdminPage />} />
          <Route path="companies/:slug"  element={<TenantDetailPage />} />
          <Route path="admin-accounts"   element={<AdminAccountsPage />} />
          <Route path="feedback"         element={<FeedbackAdminPage />} />
          <Route path="support"         element={<SupportAdminPage />} />
          <Route path="invite-codes"    element={<InviteCodesAdminPage />} />
          <Route path="billing"         element={<BillingOverviewPage />} />
          <Route path="metrics"        element={<MetricsDashboardPage />} />
          <Route path="onboarding"     element={<OnboardingConfigPage />} />
          <Route path="qa-reports"     element={<QAReportsPage />} />
          <Route path="system-health"  element={<SystemHealthPage />} />
          <Route path="progress"       element={<ProgressDashboardPage />} />
          <Route path="modules"        element={<PlatformModulesPage />} />
          <Route path="demo-modules"   element={<DemoModulesPage />} />
          <Route path="usage"          element={<UsageMeteringPage />} />
          <Route path="logs"           element={<LogConsolePage />} />
        </Route>

        <Route path="/budgets/overview"    element={<Navigate to="/budgets?tab=overview" replace />} />
        <Route path="/reports/utilization" element={<Navigate to="/financial/utilization?tab=report" replace />} />

        <Route path="/admin/info-pages"               element={<Navigate to="/admin?tab=info-pages"    replace />} />
        <Route path="/admin/webhooks"                 element={<Navigate to="/admin?tab=webhooks"       replace />} />
        <Route path="/admin/ai-settings"              element={<Navigate to="/admin?tab=ai-settings"   replace />} />
        <Route path="/settings/domains"               element={<Navigate to="/admin?tab=domains"        replace />} />
        <Route path="/settings/rehearsal-templates"   element={<Navigate to="/admin?tab=rehearsal"      replace />} />
        <Route path="/settings/sustainability"        element={<Navigate to="/admin?tab=sustainability" replace />} />
        <Route path="/notifications/settings"         element={<Navigate to="/admin?tab=notifications"  replace />} />

        <Route path="*"                   element={<NotFoundPage />} />
      </Routes>
      <CookieBanner />
      <DemoLanding />
      </Suspense>
      </SessionExpiredProvider>
    </BrowserRouter>
    </ErrorBoundary>
  )
}
